/**
 * Pod message threads (Tutoring Pods).
 *
 * Distinct from messagingService, which handles relationship-anchored 1:1 Conversations. Pod
 * threads are org-scoped and every thread is visible to the whole tutor team of that pod; there are
 * no private tutor-student channels inside a pod.
 *
 * Omitting organizationId lists threads across every pod the caller belongs to, which is what the
 * inbox uses. That is a READ-side convenience only: sending is always scoped to a single pod, and
 * there is deliberately no cross-pod send path.
 */
import api from './api'

const BASE = '/pod-threads'

const podMessagingService = {
  /** Threads across all the caller's pods, or one pod when organizationId is given. */
  async listThreads(organizationId = null) {
    const { data } = await api.get(BASE, {
      params: organizationId ? { organizationId } : {}
    })
    return data?.data?.threads || []
  },

  async getThread(threadId) {
    const { data } = await api.get(`${BASE}/${threadId}`)
    return data?.data?.thread || null
  },

  async postMessage(threadId, content) {
    const { data } = await api.post(`${BASE}/${threadId}/messages`, { content })
    return data?.data
  },

  async markRead(threadId) {
    await api.post(`${BASE}/${threadId}/read`)
  },

  /** Create a thread in one pod. audienceType: 'cohort' | 'group' | 'students'. */
  async createThread(payload) {
    const { data } = await api.post(BASE, payload)
    return data?.data?.thread
  },

  /** Total unread across pod threads, for the inbox badge. */
  async getUnreadCount() {
    const threads = await this.listThreads()
    return threads.reduce((sum, t) => sum + (t.unreadCount || 0), 0)
  }
}

export default podMessagingService
